import React, { useState, useRef, useEffect } from 'react';
import { Bell, CreditCard, Edit3, MoreVertical, FileText, User, Plus, Award } from 'lucide-react';

const FeeActions = ({
  student,
  fee,
  isVoided,
  due = 0,
  onAlertParent,
  onCollectPayment,
  onEditFee,
  onViewHistory,
  onViewStudentDrawer,
  onGenerateClearanceCert
}) => {
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const menuRef = useRef(null);

  // Close more menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
    };
    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreMenu]);

  if (isVoided) {
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => onViewHistory(student)}
          style={{
            fontSize: '11px',
            padding: '2px 7px',
            borderRadius: '4px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            borderColor: '#cbd5e1',
            color: '#475569',
            fontWeight: '600',
            backgroundColor: '#ffffff',
            height: '24px',
            lineHeight: 1
          }}
          title="View Historical Audit Logs & Receipts"
        >
          <FileText size={12} /> History
        </button>
        <button
          type="button"
          className="btn btn-sm btn-light"
          onClick={() => onEditFee(student, fee)}
          style={{
            padding: '2px 7px',
            fontSize: '11px',
            border: '1px solid #cbd5e1',
            fontWeight: '600',
            height: '24px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '2px',
            lineHeight: 1
          }}
          title="Re-assign / Correct Fee Structure"
        >
          <Edit3 size={12} /> Correct
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', justifyContent: 'center', position: 'relative' }}>
      {/* Alert Parent Button (only if balance is due) */}
      {due > 0 && (
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => onAlertParent(student, due)}
          style={{
            padding: '2px 7px',
            fontSize: '11px',
            background: '#fffbeb',
            borderColor: '#f59e0b',
            color: '#b45309',
            fontWeight: '600',
            height: '24px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '2px',
            lineHeight: 1,
            borderRadius: '4px'
          }}
          title="Send Fee Alert Notification to Parent"
        >
          <Bell size={12} /> Alert
        </button>
      )}

      {/* Collect Fee Button (if fee liability exists and not fully cleared) */}
      {fee && due > 0 && (
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => onCollectPayment(student, fee)}
          style={{
            padding: '2px 8px',
            fontSize: '11px',
            fontWeight: '600',
            height: '24px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            lineHeight: 1,
            borderRadius: '4px'
          }}
          title="Collect Fee Payment"
        >
          <CreditCard size={12} /> Collect
        </button>
      )}

      {/* Clearance Certificate Button (When fee liability exists and due is 0) */}
      {fee && due <= 0 && (Number(fee.totalFees || fee.paidFees || 0) > 0) && (
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => onGenerateClearanceCert && onGenerateClearanceCert(student, fee)}
          style={{
            padding: '2px 8px',
            fontSize: '11px',
            background: '#ecfdf5',
            borderColor: '#10b981',
            color: '#047857',
            fontWeight: '700',
            height: '24px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            lineHeight: 1,
            borderRadius: '4px'
          }}
          title="Generate Official Fee Clearance / No Dues Certificate"
        >
          <Award size={12} /> Clearance Cert
        </button>
      )}

      {/* Edit / Assign Button */}
      <button
        type="button"
        className="btn btn-sm btn-light"
        onClick={() => onEditFee(student, fee)}
        style={{
          padding: '2px 7px',
          fontSize: '11px',
          border: '1px solid #cbd5e1',
          fontWeight: '600',
          height: '24px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '2px',
          lineHeight: 1,
          borderRadius: '4px',
          backgroundColor: '#ffffff'
        }}
        title={fee ? 'Edit Fee & View Audit History' : 'Assign Fee Structure Template'}
      >
        {fee ? <Edit3 size={12} /> : <Plus size={12} />}
        <span>{fee ? 'Edit' : 'Assign'}</span>
      </button>

      {/* More Actions Popover Trigger */}
      <div style={{ position: 'relative' }} ref={menuRef}>
        <button
          type="button"
          onClick={() => setShowMoreMenu(!showMoreMenu)}
          style={{
            background: '#f8fafc',
            border: '1px solid #cbd5e1',
            borderRadius: '4px',
            padding: '0 4px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#64748b'
          }}
          title="More Student Fee Actions"
        >
          <MoreVertical size={13} />
        </button>

        {/* Dropdown Menu */}
        {showMoreMenu && (
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: '28px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              border: '1px solid #e2e8f0',
              minWidth: '180px',
              zIndex: 100,
              padding: '4px 0',
              textAlign: 'left'
            }}
          >
            <button
              type="button"
              onClick={() => {
                setShowMoreMenu(false);
                onViewStudentDrawer(student, fee);
              }}
              style={{
                width: '100%',
                padding: '6px 12px',
                border: 'none',
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '12px',
                color: '#1e293b',
                cursor: 'pointer',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f1f5f9')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <User size={14} color="#3b82f6" /> Quick View Details
            </button>

            <button
              type="button"
              onClick={() => {
                setShowMoreMenu(false);
                onViewHistory(student);
              }}
              style={{
                width: '100%',
                padding: '6px 12px',
                border: 'none',
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '12px',
                color: '#1e293b',
                cursor: 'pointer',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f1f5f9')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <FileText size={14} color="#64748b" /> Receipt & Audit History
            </button>

            <button
              type="button"
              onClick={() => {
                setShowMoreMenu(false);
                if (onGenerateClearanceCert) onGenerateClearanceCert(student, fee);
              }}
              style={{
                width: '100%',
                padding: '6px 12px',
                border: 'none',
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '12px',
                color: due <= 0 ? '#047857' : '#64748b',
                cursor: 'pointer',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = due <= 0 ? '#f0fdf4' : '#f8fafc')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <Award size={14} color={due <= 0 ? '#10b981' : '#64748b'} />
              <span>{due <= 0 ? '📜 Clearance Certificate' : 'Clearance Status (Dues: ₹' + Number(due).toLocaleString('en-IN') + ')'}</span>
            </button>

            {fee && (
              <button
                type="button"
                onClick={() => {
                  setShowMoreMenu(false);
                  onEditFee(student, fee);
                }}
                style={{
                  width: '100%',
                  padding: '6px 12px',
                  border: 'none',
                  background: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '12px',
                  color: '#1e293b',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f1f5f9')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <Edit3 size={14} color="#059669" /> Fee Correction Mode
              </button>
            )}

            {due > 0 && (
              <button
                type="button"
                onClick={() => {
                  setShowMoreMenu(false);
                  onAlertParent(student, due);
                }}
                style={{
                  width: '100%',
                  padding: '6px 12px',
                  border: 'none',
                  background: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '12px',
                  color: '#b45309',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fffbeb')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <Bell size={14} color="#d97706" /> Send Parent Reminder
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeeActions;
